import { useState, useRef, useCallback, useEffect } from 'react';
import { ApplicationConstants } from '../models/ApplicationConstants';
import { SessionStorageConsts } from '../models/SessionStorageConsts';
import { azureSearchAuthService } from '../services/AzureSearchAuthService';

export interface TokenExpiryState {
    isExpiring: boolean;
    timeRemaining: number; // in minutes
}

/**
 * Hook that silently monitors token expiry and proactively calls the backend
 * refresh endpoint (GetAccessTokenJson) when the token is within 5 minutes of expiry.
 */
export const useTokenExpiryMonitor = (): TokenExpiryState => {
    const [tokenExpiryState, setTokenExpiryState] = useState<TokenExpiryState>({
        isExpiring: false,
        timeRemaining: 0
    });

    // Guard flag prevents concurrent refresh calls
    const isRefreshing = useRef(false);

    // The existing PartnerCenter token endpoint - same one used at login
    const tokenEndpoint = process.env.REACT_APP_RSLR_ACCESS_TOKEN || '';

    //Returns minutes remaining until token expires, or null if expiry is not yet stored
    const getMinutesRemaining = useCallback((): number | null => {
        const tokenExpiryStr = sessionStorage.getItem(SessionStorageConsts.userTokenExpiry);
        if (!tokenExpiryStr) {
            return null;
        }
        const expiryDate = new Date(tokenExpiryStr);
        if (isNaN(expiryDate.getTime())) {
            return null;
        }
        const diff = expiryDate.getTime() - Date.now();
        return Math.max(0, Math.floor(diff / (1000 * 60)));
    }, []);

    const refreshTokenSilently = useCallback(async (): Promise<void> => {
        if (!tokenEndpoint || isRefreshing.current) {
            return;
        }

        isRefreshing.current = true;
        try {
            const response = await fetch(tokenEndpoint, {
                method: 'GET',
                credentials: 'include',
            });

            if (!response.ok) {
                return;
            }

            // Read the server-side expiry from the response header first (always present).
            // Fall back to the JSON body so both old and new backend versions are handled.
            const headerExpiresOn = response.headers.get('X-Token-ExpiresOn') ?? '';
            const tokenRefreshed = response.headers.get('X-Token-Refreshed') === 'true';

            const tokenJson = await response.json();
            const accessToken: string = tokenJson?.AccessToken ?? tokenJson?.accessToken ?? '';
            const expiresOn: string = headerExpiresOn || (tokenJson?.ExpiresOn ?? tokenJson?.expiresOn ?? '');

            // If the backend returned an empty token AND did not refresh it, the server-side
            // refresh failed completely (MSAL account not found, refresh token revoked, or
            // the user has been away so long the 90-day AAD refresh token expired).
            // The only recovery is a full re-authentication � redirect to the login page.
            if (!accessToken && !tokenRefreshed) {
                const loginUrl = process.env.REACT_APP_RSLR_LOGIN_API_URI || '';
                if (loginUrl) {
                    sessionStorage.clear();
                    window.location.href = loginUrl;
                }
                return;
            }

            const isValidJwt = (token: string): boolean => {
                const parts = token.split('.');
                return parts.length === 3 && parts.every(part => part.length > 0);
            };

            if (accessToken && isValidJwt(accessToken)) {
                // Always update the token in sessionStorage � the backend always returns
                // the current best token (refreshed or still-valid existing one).
                const previousToken = sessionStorage.getItem(SessionStorageConsts.nextGenAPIPCUserToken) ?? '';
                sessionStorage.setItem(SessionStorageConsts.nextGenAPIPCUserToken, accessToken);

                // If the PC user token changed, proactively invalidate the Azure Search token
                // cache so the next search call fetches a fresh token against the new PC token.
                if (accessToken !== previousToken) {
                    azureSearchAuthService.clearTokenCache();
                }
            }

            if (expiresOn) {
                const parsedExpiry = new Date(expiresOn);
                if (!isNaN(parsedExpiry.getTime()) && parsedExpiry.getTime() > Date.now()) {
                    // Always sync the expiry from the server response so getMinutesRemaining()
                    sessionStorage.setItem(SessionStorageConsts.userTokenExpiry, expiresOn);
                }
            }

            if (tokenRefreshed) {
                sessionStorage.setItem(SessionStorageConsts.userTokenRefreshedOn, new Date().toISOString());
            }
        } catch {
            // Swallow errors - refresh failure must never interrupt the user
        } finally {
            isRefreshing.current = false;
        }
    }, [tokenEndpoint]);

    /**
     * Checks current token expiry and triggers a silent refresh when within
     * the configured buffer window (AccessTokenRefreshTimeInMinutes = 5).
     */
    const checkAndRefresh = useCallback(() => {
        const minutesRemaining = getMinutesRemaining();

        // null means userTokenExpiry is not in sessionStorage yet.
        // This can happen if the user reloaded the page after login
        const hasToken = !!sessionStorage.getItem(SessionStorageConsts.nextGenAPIPCUserToken);

        if (minutesRemaining === null) {
            if (hasToken) {
                void refreshTokenSilently();
            }
            return;
        }

        // Keep timeRemaining up to date for diagnostic use; never expose isExpiring in UI
        setTokenExpiryState({ isExpiring: false, timeRemaining: minutesRemaining });

        // Only attempt a silent refresh when a token actually exists in storage
        // and is within the configured refresh buffer window.
        if (hasToken && minutesRemaining <= ApplicationConstants.AccessTokenRefreshTimeInMinutes) {
            void refreshTokenSilently();
        }
    }, [getMinutesRemaining, refreshTokenSilently]);

    useEffect(() => {
        // Check immediately on mount
        checkAndRefresh();

        // Poll every 1 minute so we have multiple attempts inside the 5-minute
        // refresh window.  A 2-minute interval left only 2-3 tries before the
        // token expired; 1 minute gives up to 5 attempts, making idle renewal
        // resilient to a single transient backend failure.
        const intervalId = setInterval(checkAndRefresh, 1 * 60 * 1000);

        // When the user returns to the tab after being away (screen locked, different tab,
        // or browser reopened), immediately check and refresh the token.
        // This covers the case where the interval was suspended by the browser while the
        // tab was hidden and the token is now near expiry or already expired.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void checkAndRefresh();
            }
        };

        // Also handle the window focus event � catches cases where the user switches back
        // to the window from another application (screen was locked / away).
        const handleFocus = () => {
            void checkAndRefresh();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [checkAndRefresh]);

    return tokenExpiryState;
};
